import type { InvitedPerson, RoomType } from './types'

/**
 * How a room's price reads to a guest.
 *
 * Pence in, pounds out. The De Vere Suite is free, and "£0 per night" would
 * read like a placeholder rather than a fact, so nothing-to-pay says so.
 */
export function formatRoomPrice(pricePence: number): string {
  if (pricePence === 0) return 'No charge'
  const pounds = pricePence / 100
  // Whole pounds are the normal case and "£190.00 per night" is needless noise,
  // but a price with pence in it must not be rounded away.
  const amount = Number.isInteger(pounds) ? pounds.toFixed(0) : pounds.toFixed(2)
  return `£${amount} per night`
}

/**
 * A monzo.me link for one room, already carrying the amount and a reference.
 *
 * The reference is the invite code, so a payment that lands says who it came
 * from without anybody having to guess from the name on the account.
 *
 * The amount goes in as pounds, since that is what monzo.me takes - whole where
 * the price is whole, two decimal places otherwise.
 */
export function monzoPayUrl(handle: string, pricePence: number, inviteCode: string): string {
  const pounds = pricePence / 100
  const amount = Number.isInteger(pounds) ? String(pounds) : pounds.toFixed(2)
  return `https://monzo.me/${encodeURIComponent(handle)}/${amount}?d=${encodeURIComponent(inviteCode)}`
}

/** How a room's remaining count reads, or null when there is nothing to say. */
export function formatAvailability(room: RoomType): string {
  const left = Math.max(room.capacity - room.reserved, 0)
  if (left === 0) return 'Fully booked'
  if (left === room.capacity) return `${left} available`
  return `${left} of ${room.capacity} left`
}

/**
 * A room type's price, or null if it cannot be found.
 *
 * Null rather than 0, because "free" and "we do not know" are different answers
 * and a caller comparing two prices must not mistake one for the other.
 */
export function priceOf(
  types: readonly RoomType[] | null,
  roomTypeId: string | undefined,
): number | null {
  if (types === null || roomTypeId === undefined) return null
  return types.find((room) => room.id === roomTypeId)?.price_pence ?? null
}

/**
 * Whether anybody on the invite is coming, which is what allows it to hold a
 * room.
 *
 * "Any", not "all", and it matches the same rule on the server: when one of a
 * couple drops out the other still needs somewhere to sleep, so a partial
 * decline must not cost them the booking.
 */
export function anyAttending(people: readonly InvitedPerson[]): boolean {
  return people.some((person) => person.response === 'yes')
}
